<template>
  <InputSkeleton :name="name"
                 :errors="errors"
                 class="TimePickerInput"
                 :class="{
                   [`TimePickerInput--${size}`]: !!size,
                   'TimePickerInput--disabled': disabled,
                   'TimePickerInput--readonly': readonly,
                   'TimePickerInput--invalid': invalid || !!errors.length,
                   'TimePickerInput--highlight': isHighlighted,
                   'TimePickerInput--float-label': floatLabel,
                 }">
    <template v-if="$slots.default || label" #label>
      <slot v-if="$slots.default" />
      <template v-else>{{ label }}</template>
    </template>
    <div class="TimePickerInput__input">
      <input ref="input"
             :id="name"
             :value="modelValue"
             :name="name"
             :autofocus="autofocus"
             :disabled="disabled"
             :readonly="readonly"
             :placeholder="placeholder"
             type="text"
             :autocomplete="autocomplete"
             data-test-id="input"
             @input="onInput"
             @focus="onFocus"
             @blur="onBlur"
             @keydown="onKeydown"
      />
      <div class="TimePickerInput__icon">
        <Icon name="clock2" />
      </div>
    </div>
  </InputSkeleton>
</template>

<script setup lang="ts">
  import Inputmask from 'inputmask';
  import { computed, onMounted, ref } from 'vue';
  import { UiSize } from '../../../constants';
  import Icon from '../../Icon.vue';
  import InputSkeleton from '../InputSkeleton.vue';
  import { InputErrorObject } from './types';

  const props = withDefaults(defineProps<{
    modelValue: string;
    name: string;
    label?: string;
    placeholder?: string;
    size?: UiSize;
    autofocus?: boolean;
    autocomplete?: 'on' | 'off';
    invalid?: boolean;
    errors?: InputErrorObject[];
    disabled?: boolean;
    readonly?: boolean;
    highlight?: boolean;
    floatLabel?: boolean;
  }>(), {
    autofocus: false,
    autocomplete: 'off',
    size: UiSize.MD,
    placeholder: 'чч:мм',
    errors: () => [],
    floatLabel: false,
  });

  const emit = defineEmits<{
    (e: 'update:model-value', modelValue: string): void;
    (e: 'blur', modelValue: string): void;
    (e: 'focus'): void;
    (e: 'keydown', event: KeyboardEvent): void;
  }>();

  const input = ref<HTMLInputElement>();
  const inputMask = ref<Inputmask.Instance>();

  const isHighlighted = computed<boolean>(() => Boolean(props.highlight && !props.modelValue));

  const onInput = (event: Event) => {
    emit('update:model-value', (event.target as HTMLInputElement).value);
  };

  const onFocus = () => {
    input.value.select();
    emit('focus');
  };

  const onKeydown = (event: KeyboardEvent) => {
    emit('keydown', event);
  };

  const onBlur = (event: Event) => {
    emit('blur', (event.target as HTMLInputElement).value);
  };

  const maskEnable = () => {
    const options: Inputmask.Options = {
      showMaskOnFocus: false,
      placeholder: ' ',
      showMaskOnHover: false,
    };

    options.alias = 'datetime';
    options.inputFormat = 'HH:MM';
    inputMask.value = new Inputmask(options).mask(input.value);
  };

  onMounted(() => {
    maskEnable();
  });
</script>

<style lang="scss" scoped>
  @import '../../../assets/scss/_variables.scss';
  @import '../../../assets/scss/_mixins.scss';

  .TimePickerInput {
    display: flex;
    flex-direction: column;
    width: 100%;

    &__input {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;
      height: 100%;
    }

    &__label {
      display: block;
      margin-bottom: 10px;
      font-weight: 600;
    }

    label {
      white-space: nowrap;
    }

    input {
      @include transition($transition-function, $transition-duration, border-color);

      width: 100%;
      border: 1px solid $color-grey-75;
      border-radius: 4px;
      background-color: $color-white;
      line-height: 1;
      font-size: 14px;
    }

    &__icon {
      position: absolute;
      width: 16px;
      height: 16px;
      color: $color-grey-100;
      right: 10px;
    }

    &--sm input {
      // ...
    }

    &--md input {
      height: 30px;
      padding-left: 10px;
      padding-right: 10px;
    }

    &--lg input {
      height: 40px;
      padding-left: 10px;
      padding-right: 10px;
    }

    &--xl input {
      height: 50px;
      padding-left: 15px;
      padding-right: 15px;
    }

    &--highlight input {
      background-color: #ffffd2;
    }

    &--disabled input {
      background-color: $color-grey-50;
    }

    &--disabled input {
      cursor: not-allowed;
    }

    input:focus {
      outline: none;
      border-color: $color-second;
    }

    input::placeholder {
      color: $color-grey-100;
    }

    &__toggle {
      cursor: pointer;
    }

    &--invalid input {
      border-color: $color-red-100;
    }

    &--invalid input::placeholder {
      color: $color-red-100;
    }

    &--invalid .svg-icon {
      color: $color-red-100;
    }

    &--float-label {
      @include float-label('TimePickerInput');
    }
  }
</style>
